import { ReactNode, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { AuthLoadingView } from 'shared-ui/lib/components/auth/AuthLoadingView';
import { GenericFormScene } from 'shared-ui/lib/components/generic-form-scene/GenericFormScene';
import { setupQueryConfig } from 'shared-ui/lib/config/query.config';
import { useHandleSubmit } from 'shared-ui/lib/hooks/apollo/use-handle-submit';
import { routesConfig } from '../../config/routes.config';
import UserContext from '../../contexes/UserContext';
import { createServiceCallApplicationForm } from '../../form/service-call-application/service-call-application.form';
import {
  useServiceCallAccessStatusQuery,
  useServiceCallApplySceneUsersQuery,
  useSubmitServiceCallApplicationMutation,
} from '../../generated/graphql';
import { UserLevel } from '../../hooks/use-can';

const StatusCard = ({ title, children }: { title: string; children: ReactNode }): JSX.Element => (
  <div className="mx-auto w-full max-w-3xl sm:px-2">
    <div className="bg-white py-8 px-6 sm:px-10 shadow rounded-lg">
      <h2 className="text-lg leading-6 font-bold text-primary">{title}</h2>
      <div className="mt-3 text-sm text-gray-600">{children}</div>
    </div>
  </div>
);

const isEligibleApplicant = (user: {
  level?: string | null;
  isPartnerAccountOwner: boolean;
}): boolean =>
  user.level === UserLevel.MASTER || user.level === UserLevel.ADMIN || user.isPartnerAccountOwner;

export const ServiceCallApplyScene = (): JSX.Element => {
  const { t } = useTranslation('ServiceCallApplyScene');
  const navigate = useNavigate();
  const [currentUser] = useContext(UserContext);

  const { data, loading: statusLoading } = useServiceCallAccessStatusQuery(setupQueryConfig());
  const { data: usersData, loading: usersLoading } =
    useServiceCallApplySceneUsersQuery(setupQueryConfig());

  const [submitApplication, { loading }] = useSubmitServiceCallApplicationMutation({
    onCompleted: (result) => {
      if (!result) return;
      toast.success(t('submitSuccess'));
      navigate(routesConfig.DASHBOARD);
    },
  });

  const handleSubmit = useHandleSubmit(submitApplication);

  const form = useMemo(() => {
    const eligibleUsers = (usersData?.users.list ?? []).filter(isEligibleApplicant);
    const emailOptions = eligibleUsers.map((teamUser) => ({
      value: teamUser.email,
      label: `${teamUser.name} (${teamUser.email})`,
    }));
    const defaultEmail = emailOptions.some((option) => option.value === currentUser.email)
      ? currentUser.email
      : '';
    const officialName = currentUser.partner?.name ?? '';

    return createServiceCallApplicationForm(emailOptions, defaultEmail, officialName);
  }, [usersData, currentUser.email, currentUser.partner?.name]);

  if (statusLoading) return <AuthLoadingView />;

  const accessState = data?.myServiceCallAccessState;

  if (accessState === 'approved' || data?.partner?.hasServiceCallAccess) {
    return (
      <StatusCard title={t('accessActiveTitle')}>
        <p>{t('accessActiveText')}</p>
      </StatusCard>
    );
  }

  if (accessState === 'inactive') {
    return (
      <StatusCard title={t('accessInactiveTitle')}>
        <p>{t('accessInactiveText')}</p>
        <p className="mt-4 font-medium text-gray-800">{t('accessInactiveContact')}</p>
        <p className="mt-1">
          {t('accessInactivePhoneLabel')}{' '}
          <a
            className="text-primary underline"
            href={`tel:${t('accessInactivePhone').replace(/\s/g, '')}`}
          >
            {t('accessInactivePhone')}
          </a>
        </p>
        <p className="mt-1">
          {t('accessInactiveEmailLabel')}{' '}
          <a className="text-primary underline" href={`mailto:${t('accessInactiveEmail')}`}>
            {t('accessInactiveEmail')}
          </a>
        </p>
      </StatusCard>
    );
  }

  if (accessState === 'pending') {
    return (
      <StatusCard title={t('accessPendingTitle')}>
        <p>{t('accessPendingText')}</p>
      </StatusCard>
    );
  }

  if (usersLoading) return <AuthLoadingView />;

  return (
    <GenericFormScene
      type="create"
      tNS="ServiceCallApplyScene"
      title={t('title')}
      onSubmit={handleSubmit}
      form={form}
      isSubmitLoading={loading}
      backLink={routesConfig.DASHBOARD}
    />
  );
};
